import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import dayjs from "dayjs"
import { Truck } from "iconsax-reactjs"
import { CheckIcon, Loader2, RefreshCcwIcon, XIcon } from "lucide-react"
import { useMemo, useState } from "react"
import PullToRefresh from "react-simple-pull-to-refresh"
import { toast } from "sonner"

import { LoadingSpinner } from "@/components/shared"
import { GoBack } from "@/components/shared/layout"
import { Button } from "@/components/ui/button"
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import { Sheet, SheetContent, SheetTitle } from "@/components/ui/sheet"
import { AffiliateProductDetails } from "@/features/jobs/affiliates/components/affiliate-product"
import { AffiliateCampaignDetail } from "@/features/jobs/explore/components/campaign/campaign-detail-section"
import { getDraftsByPotentialId } from "@/features/jobs/manage/api/draft.api"
import {
  checkStatusSubmission,
  fetchSubmissionDetails,
  refreshSampleApplication,
  rejectInvitedReceiveSample
} from "@/features/jobs/manage/api/manage.api"
import { UploadDraftModal } from "@/features/jobs/manage/components/draft"
import { OrderTrackingSheet } from "@/features/jobs/manage/components/order-tracking-sheet"
import {
  REASON_REJECT_ACCEPTANCE_MESSAGE,
  REFRESH_SAMPLE_APPLICATION_MESSAGE
} from "@/features/jobs/manage/constants"
import {
  getSampleRefreshCooldownMessage,
  isSampleRefreshOnCooldown,
  setSampleRefreshCooldown
} from "@/features/jobs/manage/utils/sample-refresh-cooldown"
import ScreenLayout from "@/layouts/screen"
import {
  EAffiliateOrderType,
  ECheckKolSubmissionStatus,
  EFilterStatus,
  ESubmitStatus
} from "@/modules/helpers/enum"
import { useCurrentUser } from "@/modules/helpers/hooks/shared"
import { useShowSubmission } from "@/modules/helpers/hooks/shared/use-show-submission"
import { cn } from "@/modules/helpers/hooks/shared/utils"
import { TEXT_STATUS_DRAFT, TEXT_STATUS_DRAFT_COLOR } from "@/modules/helpers/options"
import { openTikTokShowcaseAuth, sendHaptic, sendOpenLink } from "@/modules/helpers/utils"
import type { SubmissionOrder } from "@/modules/types/jobs/campaign"

interface StatusStep {
  label: string
  date?: string
  isCompleted?: boolean
  hasIcon?: boolean
  subLabel?: string
  action?: {
    label: string
    onCallBack: () => void
  }
}

// const DEADLINE_DAYS_IN_APP = 7
// const DEADLINE_DAYS_OUT_APP = 14

const getStatusLevel = (isCast: boolean): Partial<Record<ECheckKolSubmissionStatus, number>> => {
  const base = {
    [ECheckKolSubmissionStatus.BRAND_APPROVED]: 1,
    [ECheckKolSubmissionStatus.ATTACHED_LINK]: 2,
    [ECheckKolSubmissionStatus.AUTHORIZED_ORDER_SAMPLE]: 3,
    [ECheckKolSubmissionStatus.WAITING_FOR_DELIVERY]: 4,
    [ECheckKolSubmissionStatus.REJECTED]: 5,
    [ECheckKolSubmissionStatus.OVERDUE]: 5,
    [ECheckKolSubmissionStatus.WORKING]: 5
  }

  if (isCast) {
    return {
      ...base,
      [ECheckKolSubmissionStatus.PENDING_ACCEPTANCE]: 6,
      [ECheckKolSubmissionStatus.FINISHED]: 7,
      [ECheckKolSubmissionStatus.ACCEPTED_ACCEPTANCE]: 7,
      [ECheckKolSubmissionStatus.REJECTED_ACCEPTANCE]: 7,
      [ECheckKolSubmissionStatus.PAYMENT_COMPLETED]: 8
    }
  }

  return {
    ...base,
    [ECheckKolSubmissionStatus.FINISHED]: 6
  }
}

const getLastLevel = (statusList: ECheckKolSubmissionStatus[], isCast: boolean): number => {
  const STATUS_LEVEL = getStatusLevel(isCast)
  return Math.max(...(statusList?.map((status) => STATUS_LEVEL[status] ?? 0) ?? [0]))
}

const getIsCompleted = (
  currentStatus: ECheckKolSubmissionStatus[],
  checkStatus: ECheckKolSubmissionStatus,
  isCast: boolean
): boolean | undefined => {
  const STATUS_LEVEL = getStatusLevel(isCast)
  const lastLevel = getLastLevel(currentStatus, isCast)
  if (lastLevel > (STATUS_LEVEL[checkStatus] ?? 0)) {
    return currentStatus?.includes(checkStatus)
  }
  if (lastLevel + 1 === (STATUS_LEVEL[checkStatus] ?? 0) && (STATUS_LEVEL[checkStatus] ?? 0) < 4) {
    return currentStatus?.includes(checkStatus)
  }
  return currentStatus?.includes(checkStatus) || undefined
}

const SubmissionDetailsSheet = () => {
  const { id, setId } = useShowSubmission()
  const { user } = useCurrentUser()
  const [showDraftsManager, setShowDraftsManager] = useState(false)
  const [showOrderTracking, setShowOrderTracking] = useState(false)
  const [showRejectConfirm, setShowRejectConfirm] = useState(false)
  const [isRefreshingSample, setIsRefreshingSample] = useState(false)

  const queryClient = useQueryClient()

  const rejectMutation = useMutation({
    mutationFn: () => rejectInvitedReceiveSample(id as string),
    onSuccess: () => {
      sendHaptic()
      setShowRejectConfirm(false)
      setId("")
      toast.success("Bạn đã từ chối lời mời")
      queryClient.invalidateQueries({ queryKey: ["submission"] })
    }
  })

  const {
    data: submission,
    isLoading,
    isError,
    refetch
  } = useQuery({
    queryKey: [`submission_${id}`],
    queryFn: () => fetchSubmissionDetails(id as string),
    enabled: !!id
  })

  const orders = submission?.data?.data?.ordersTap

  const affCampaigns = submission?.data?.data?.affCampaigns
  const isSendOutside = affCampaigns?.sendVia === EAffiliateOrderType.SEND_OUTSIDE
  const submissionData = submission?.data?.data

  // Fetch drafts
  const {
    data: draftList,
    refetch: refetchDrafts,
    isLoading: isLoadingDrafts
  } = useQuery({
    queryKey: [
      `drafts_of_potential_${affCampaigns?.potentials?.[0]?.id}_${submissionData?.id}`,
      submissionData?.status,
      affCampaigns?.potentials?.[0]?.id,
      submissionData?.id
    ],
    enabled:
      !!affCampaigns?.potentials?.[0]?.id &&
      !!submissionData?.id &&
      [
        ESubmitStatus.WORKING,
        ESubmitStatus.FINISHED,
        ESubmitStatus.OVERDUE,
        ESubmitStatus.PENDING_ACCEPTANCE,
        ESubmitStatus.REJECTED_ACCEPTANCE,
        ESubmitStatus.ACCEPTED_ACCEPTANCE
      ].includes(submissionData?.status),
    queryFn: () => getDraftsByPotentialId(affCampaigns?.potentials?.[0]?.id, submissionData?.id)
  })

  const {
    data: checkStatus,
    isLoading: isLoadingStatus,
    refetch: refetchCheckStatus
  } = useQuery({
    queryKey: [`check_status_${affCampaigns?.potentials?.[0]?.id}_${id}`],
    enabled:
      !!id &&
      !!affCampaigns?.potentials?.[0]?.id &&
      submissionData?.status !== ESubmitStatus.REJECTED,
    queryFn: () => checkStatusSubmission(id as string)
  })

  const statusList = useMemo(() => checkStatus?.data?.data || [], [checkStatus?.data?.data])
  const drafts = draftList?.data?.data?.list || []

  const handleRefresh = async () => {
    await refetchDrafts()
    await refetch()
    sendHaptic()
  }

  const handleRefreshSampleStatus = async () => {
    if (!id || isRefreshingSample) return

    if (isSampleRefreshOnCooldown(id)) {
      toast.info(getSampleRefreshCooldownMessage(id))
      return
    }

    sendHaptic()
    setIsRefreshingSample(true)

    try {
      const res = await refreshSampleApplication(id)
      const message = res?.data?.message

      if (message === "REFRESH_SAMPLE_APPLICATION_COOLDOWN") {
        setSampleRefreshCooldown(id)
        toast.info(getSampleRefreshCooldownMessage(id))
        return
      }

      if (res?.data?.data !== true) {
        toast.error(REFRESH_SAMPLE_APPLICATION_MESSAGE.REFRESH_SAMPLE_APPLICATION_NOT_FOUND, {
          richColors: true,
          className: "bg-red-500 text-white text-sm"
        })
        return
      }

      setSampleRefreshCooldown(id)
      await Promise.all([refetchCheckStatus(), refetch()])
      toast.success("Đã cập nhật")
    } catch {
      toast.error("Làm mới thất bại")
    } finally {
      setIsRefreshingSample(false)
    }
  }

  const handleClose = () => {
    sendHaptic()
    setId("")
  }

  // Calculate status steps based on submission data
  const statusSteps: StatusStep[] = useMemo(() => {
    if (!submissionData) return []

    if (submissionData?.status === ESubmitStatus.REJECTED) {
      return [
        {
          label: "Đăng ký",
          date: undefined,
          subLabel: "",
          isCompleted: true
        },
        {
          label: "Nhãn hàng từ chối",
          date: undefined,
          subLabel: "",
          isCompleted: false
        }
      ]
    }

    let finishLabel = "Hoàn thành"
    let finishIsCompleted = getIsCompleted(
      statusList,
      ECheckKolSubmissionStatus.FINISHED,
      !!affCampaigns?.cashAmount
    )

    if (affCampaigns?.cashAmount) {
      if (statusList.includes(ECheckKolSubmissionStatus.REJECTED_ACCEPTANCE)) {
        finishLabel = "Không đạt"
        if (submissionData?.reasonRejectAcceptance) {
          const reason =
            REASON_REJECT_ACCEPTANCE_MESSAGE[
              submissionData.reasonRejectAcceptance as unknown as keyof typeof REASON_REJECT_ACCEPTANCE_MESSAGE
            ]
          if (reason) {
            finishLabel = `Không đạt: ${reason}`
          }
        }
        finishIsCompleted = false
      }
      if (statusList.includes(ECheckKolSubmissionStatus.ACCEPTED_ACCEPTANCE)) {
        finishLabel = "Hoàn tất nghiệm thu"
        finishIsCompleted = true
      }
    }

    return [
      {
        label: "Yêu cầu nhận mẫu",
        date: undefined,
        subLabel: "",
        isCompleted: getIsCompleted(
          statusList,
          ECheckKolSubmissionStatus.REQUESTED_RECEIVE_SAMPLE,
          !!affCampaigns?.cashAmount
        )
      },
      {
        label: "Được duyệt",
        date: undefined,
        subLabel: "",
        isCompleted: getIsCompleted(
          statusList,
          ECheckKolSubmissionStatus.BRAND_APPROVED,
          !!affCampaigns?.cashAmount
        )
      },
      {
        label: "Chờ nhận hàng",
        date: undefined,
        subLabel: "",
        isCompleted: getIsCompleted(
          statusList,
          ECheckKolSubmissionStatus.WAITING_FOR_DELIVERY,
          !!affCampaigns?.cashAmount
        )
      },
      {
        label: affCampaigns?.cashAmount ? "Bản nháp và đường dẫn" : "Nhập đường dẫn",
        subLabel: statusList.includes(ECheckKolSubmissionStatus.OVERDUE) ? "Quá hạn" : "",
        date: undefined,
        isCompleted:
          !statusList.includes(ECheckKolSubmissionStatus.OVERDUE) &&
          getIsCompleted(statusList, ECheckKolSubmissionStatus.WORKING, !!affCampaigns?.cashAmount)
      },
      ...(affCampaigns?.cashAmount
        ? [
            {
              label: "Đang đợi nghiệm thu",
              subLabel: "",
              date: undefined,
              isCompleted: getIsCompleted(
                statusList,
                ECheckKolSubmissionStatus.PENDING_ACCEPTANCE,
                true
              )
            }
          ]
        : []),
      {
        label: finishLabel,
        date: undefined,
        subLabel: "",
        isCompleted: finishIsCompleted
      }
    ]
  }, [statusList, submissionData, affCampaigns])

  const handleAuthorize = () => {
    if (user?.id) {
      openTikTokShowcaseAuth(user.id)
    }
  }

  const handleManageDrafts = () => {
    setShowDraftsManager(true)
  }

  return (
    <>
      <Sheet open={!!id} modal>
        <SheetContent className="w-full !bg-transparent border-none" side="right" hiddenClose>
          <SheetTitle className="sr-only">Chi tiết bài đăng</SheetTitle>
          <ScreenLayout transition="none">
            <div
              className={cn(
                "flex flex-col h-screen absolute top-0 z-50 w-full max-h-[100dvh] items-center justify-center bg-white text-black transition-all duration-300",
                {
                  "opacity-0 z-0": !isLoading
                }
              )}
            >
              <LoadingSpinner className="w-32 h-32" />
            </div>

            <div
              className={cn("flex flex-col relative z-10 h-full transition-all duration-300", {
                "opacity-0": isLoading
              })}
            >
              {(isError || (!isLoading && !submissionData)) && (
                <div className="absolute inset-0 flex flex-col items-center justify-center bg-white z-50 p-4">
                  <p className="text-gray-500 mb-4 text-center">
                    {isError ? "Đã có lỗi xảy ra khi tải dữ liệu." : "Không tìm thấy thông tin."}
                  </p>
                  <Button onClick={() => refetch()} variant="outline">
                    Thử lại
                  </Button>
                  <div className="mt-8">
                    <GoBack onClick={handleClose} title="Quay lại" className="bg-transparent" />
                  </div>
                </div>
              )}
              {!showDraftsManager && (
                <div className="bg-white top-0 sticky z-20">
                  <GoBack onClick={handleClose} />
                </div>
              )}
              {/* Content */}
              <div className="flex-1 overflow-scroll">
                <PullToRefresh
                  onRefresh={handleRefresh}
                  pullingContent=""
                  className="flex-1 h-full"
                  refreshingContent={<LoadingSpinner className="w-20 h-20" />}
                  maxPullDownDistance={80}
                >
                  <div className="flex-1 h-full flex flex-col">
                    <AffiliateCampaignDetail
                      isShowStatus={true}
                      data={affCampaigns}
                      status={
                        <SubmissionStatusTimeline
                          steps={statusSteps}
                          expiresAt={submissionData?.expiresAt}
                          workingAt={submissionData?.workingAt}
                          draftCount={submissionData?.totalClip}
                          drafts={drafts}
                          isCast={!!submissionData?.affCampaigns?.cashAmount}
                          isLoading={isLoadingStatus}
                          order={submissionData?.ordersTap}
                          type={submissionData?.affCampaigns?.type}
                          onRefreshSampleStatus={handleRefreshSampleStatus}
                          isRefreshingSample={isRefreshingSample}
                        />
                      }
                    />
                    <div className="px-4 pb-8 bg-background pt-4 rounded-t-3xl">
                      <AffiliateProductDetails
                        id={submissionData?.affCampaigns?.products?.id}
                        layoutBanner="upper"
                        isShowAction={false}
                        data={submissionData?.affCampaigns?.products}
                        preload={true}
                      />
                    </div>
                  </div>
                </PullToRefresh>
              </div>

              {submissionData?.status !== ESubmitStatus.REJECTED && (
                <div className="p-4 flex gap-2 z-20 bg-white sticky bottom-0 left-0 right-0 w-full empty:hidden">
                  {/* <Button
                    variant={"outline"}
                    className="w-18 h-12 border-primary-800 rounded-xl font-medium"
                    onClick={() => {
                      sendHaptic()
                      const finalConversationCode =
                        submissionData?.conversationCode ||
                        useShowSubmission.getState().conversationCode

                      if (!finalConversationCode) {
                        console.warn("Missing conversation code")
                        return
                      }

                      router.navigate({
                        to: "/manage/chat/$code",
                        params: { code: finalConversationCode },
                        search: {
                          shopName: campaign?.shopName,
                          otherUserId: campaign?.createdBy,
                          submissionId: submissionData?.id
                        }
                      } as any)
                    }}
                  >
                    <MessageNotif size={16} color="#045664" />
                  </Button> */}
                  {(submissionData?.status as string) === EFilterStatus.INVITED_RECEIVE_SAMPLE ? (
                    <>
                      <Button
                        variant="destructive"
                        size="lg"
                        className="w-1/2 h-12 rounded-xl font-medium"
                        disabled={rejectMutation.isPending}
                        onClick={() => {
                          sendHaptic()
                          setShowRejectConfirm(true)
                        }}
                      >
                        {rejectMutation.isPending && <Loader2 className="w-4 h-4 animate-spin" />}
                        Từ chối lời mời
                      </Button>
                      <SubmissionActionButton
                        isLoading={isLoading}
                        status={submissionData?.status}
                        isAuthCreatorTap={!!user?.isAuthCreatorTap}
                        onAuthorize={handleAuthorize}
                        onManageDrafts={handleManageDrafts}
                        isCast={!!submissionData?.affCampaigns?.cashAmount}
                        link={submissionData?.requestProductSamples?.products?.link}
                        isSendOutside={isSendOutside}
                      />
                    </>
                  ) : (
                    <>
                      {orders && (
                        <Button
                          variant={"outline"}
                          className="w-18 h-12 border-primary-800 rounded-xl font-medium"
                          onClick={() => {
                            sendHaptic()
                            setShowOrderTracking(true)
                          }}
                        >
                          <Truck size={16} color="#045664" />
                        </Button>
                      )}
                      <SubmissionActionButton
                        isLoading={isLoading}
                        status={submissionData?.status}
                        isAuthCreatorTap={!!user?.isAuthCreatorTap}
                        onAuthorize={handleAuthorize}
                        onManageDrafts={handleManageDrafts}
                        isCast={!!submissionData?.affCampaigns?.cashAmount}
                        link={submissionData?.requestProductSamples?.products?.link}
                        isSendOutside={isSendOutside}
                      />
                    </>
                  )}
                </div>
              )}
            </div>
          </ScreenLayout>
        </SheetContent>
      </Sheet>

      <UploadDraftModal
        loading={isLoadingDrafts}
        workingAt={submissionData?.workingAt || ""}
        isCastCamp={!!submissionData?.affCampaigns?.cashAmount}
        open={showDraftsManager}
        onClose={() => setShowDraftsManager(false)}
        potentials={affCampaigns?.potentials || []}
        drafts={drafts}
        disabled={false}
        refetch={handleRefresh}
        totalClip={submissionData?.totalClip || 0}
        submitStatus={submissionData?.status || ESubmitStatus.APPLIED}
        submissionId={submissionData?.id || ""}
      />

      {/* Order Tracking Sheet */}
      <OrderTrackingSheet
        open={showOrderTracking}
        onClose={() => setShowOrderTracking(false)}
        order={orders}
      />

      <Dialog open={showRejectConfirm} onOpenChange={setShowRejectConfirm}>
        <DialogContent className="w-4/5">
          <DialogHeader>
            <DialogTitle className="text-sm">
              Bạn có chắc chắn muốn từ chối lời mời nhận mẫu?
            </DialogTitle>
          </DialogHeader>
          <p className="text-xs text-gray-600 mt-2">
            Sau khi từ chối, bạn sẽ không thể tham gia chiến dịch này.
          </p>
          <div className="flex gap-2 justify-end mt-4">
            <Button
              variant="outline"
              onClick={() => {
                sendHaptic()
                setShowRejectConfirm(false)
              }}
              disabled={rejectMutation.isPending}
            >
              Hủy
            </Button>
            <Button
              variant="destructive"
              onClick={() => {
                sendHaptic()
                rejectMutation.mutate()
              }}
              disabled={rejectMutation.isPending}
            >
              {rejectMutation.isPending && <Loader2 className="w-4 h-4 animate-spin" />}
              Từ chối
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  )
}

// Submission Status Timeline Component (same as in submission.tsx)
interface SubmissionStatusTimelineProps {
  steps: StatusStep[]
  workingAt?: string
  draftCount?: number
  drafts: any[]
  isCast: boolean
  isLoading?: boolean
  type?: string
  order?: SubmissionOrder
  expiresAt?: string
  onRefreshSampleStatus?: () => void
  isRefreshingSample?: boolean
}

const SubmissionStatusTimeline = ({
  steps,
  workingAt,
  draftCount,
  drafts,
  isCast,
  isLoading,
  expiresAt,
  type: _type,
  order: _order,
  onRefreshSampleStatus,
  isRefreshingSample
}: SubmissionStatusTimelineProps) => {
  // let days = DEADLINE_DAYS_OUT_APP

  // if (
  //   order &&
  //   order?.ordersLogistics?.length &&
  //   type === EAffiliateOrderType.NORMAL &&
  //   dayjs(workingAt).isAfter(dayjs("2026-01-19"), "day")
  // ) {
  //   days = DEADLINE_DAYS_IN_APP
  // }

  return (
    <div className="flex flex-col rounded-xl transition-transform">
      {!!workingAt && (
        <div className="py-2 bg-background text-background px-3 my-2 rounded-md">
          <p className="text-xs font-normal text-primary-800">
            Bạn cần hoàn thành chiến dịch trước ngày {dayjs(expiresAt).format("DD/MM/YYYY")}
          </p>
        </div>
      )}

      <div className="flex flex-col gap-1 mt-2">
        <span className="text-sm font-semibold text-black py-2">Tiến độ</span>
        {isLoading
          ? // Skeleton loading for steps
            Array.from({ length: 5 }).map((_, index) => (
              <div key={index} className="flex gap-3 pl-2 animate-pulse">
                <div className="flex flex-col items-center">
                  <div className="w-4.5 h-4.5 rounded-full bg-gray-200" />
                  {index < 4 && <div className="w-px flex-1 mt-1 bg-gray-200 min-h-[24px]" />}
                </div>
                <div className="flex-1 pb-3">
                  <div className="h-4 bg-gray-200 rounded w-32" />
                </div>
              </div>
            ))
          : steps.map((step, index) => (
              <div key={index} className="flex gap-3 pl-2">
                <div className="flex flex-col items-center">
                  <div
                    className={cn(
                      "w-4.5 h-4.5 rounded-full border flex items-center justify-center",
                      step.isCompleted === true && "border-green-500 bg-green-500",
                      step.isCompleted === false && "border-red-500 bg-red-500",
                      step.isCompleted === undefined && "border-gray-300 bg-white"
                    )}
                  >
                    {step.isCompleted === true && <CheckIcon size={14} color="white" />}
                    {step.isCompleted === false && <XIcon size={14} color="white" />}
                  </div>
                  {index < steps.length - 1 && (
                    <div
                      className={cn(
                        "w-px flex-1 mt-1",
                        step.isCompleted ? "bg-green-500" : "bg-gray-300"
                      )}
                    />
                  )}
                </div>

                <div className="flex-1 pb-3">
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-medium text-black">{step.label} </span>
                      {step.label === "Yêu cầu nhận mẫu" && onRefreshSampleStatus && (
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-6 px-2 text-xs text-primary-800"
                          onClick={onRefreshSampleStatus}
                          disabled={isRefreshingSample}
                        >
                          {isRefreshingSample ? (
                            <Loader2 className="w-3.5 h-3.5 animate-spin" />
                          ) : (
                            <RefreshCcwIcon size={14} />
                          )}
                          Làm mới
                        </Button>
                      )}
                      {step.subLabel && (
                        <span className="text-xss text-red-500 bg-red-400/20 px-2 py-0.5 rounded-full border border-red-500 font-medium whitespace-nowrap">
                          {step.subLabel}
                        </span>
                      )}
                    </div>
                    {step.action && !step.isCompleted && (
                      <div
                        className="w-fit text-red-500 underline rounded-xl text-xs font-medium"
                        onClick={step.action.onCallBack}
                      >
                        {step.action.label}
                      </div>
                    )}
                  </div>

                  {/* Special nested UI for draft & link step */}
                  {step.label === (isCast ? "Bản nháp và đường dẫn" : "Nhập đường dẫn") && (
                    <div className="mt-2 ml-4 space-y-3">
                      {(() => {
                        // Group drafts by main version (1, 2, 3, etc.)
                        const groupedDrafts: { [key: number]: any[] } = {}
                        drafts?.forEach((draft) => {
                          const mainVersion = Math.floor(Number(draft.version))
                          if (!groupedDrafts[mainVersion]) {
                            groupedDrafts[mainVersion] = []
                          }
                          groupedDrafts[mainVersion].push(draft)
                        })

                        // Always show main versions from 1 to draftCount
                        const mainVersions = Array.from(
                          { length: draftCount || 0 },
                          (_, i) => i + 1
                        )

                        return mainVersions.map((mainVersion) => {
                          const versionDrafts = groupedDrafts[mainVersion] || []

                          // Sort drafts within version (1, 1.1, 1.2, etc.)
                          const sortedDrafts = [...versionDrafts].sort(
                            (a, b) => Number(a.version) - Number(b.version)
                          )

                          // Get the primary draft ONLY if it has exact version match (2.0, not 2.1)
                          const primaryDraft = sortedDrafts.find(
                            (d) => Number(d.version) === mainVersion
                          )

                          // Sub-versions are all drafts that don't match exact main version
                          const subVersions = sortedDrafts.filter(
                            (d) => Number(d.version) !== mainVersion
                          )

                          const hasDraft = !!primaryDraft
                          const hasSubmitUrl = !!primaryDraft?.submitUrl
                          const status = primaryDraft?.status

                          return (
                            <div key={mainVersion} className="space-y-3 mt-3">
                              <div className="flex items-center gap-3">
                                <div
                                  className={cn(
                                    "w-3.5 h-3.5 rounded-full border flex items-center justify-center",
                                    hasDraft && "border-green-500 bg-green-500",
                                    !hasDraft && "border-gray-300 bg-white"
                                  )}
                                >
                                  {hasDraft && <CheckIcon size={12} color="white" />}
                                </div>
                                <span className="text-xs font-medium text-black">
                                  {isCast ? "Bản nháp" : "Bài đăng"} {mainVersion}{" "}
                                  {!!status && (
                                    <span className={TEXT_STATUS_DRAFT_COLOR[status]}>
                                      {TEXT_STATUS_DRAFT[status]}
                                    </span>
                                  )}
                                </span>
                              </div>
                              <div className="ml-4 space-y-3">
                                {isCast && (
                                  <div className="flex items-center gap-2">
                                    <div
                                      className={cn(
                                        "w-3 h-3 rounded-full border flex items-center justify-center",
                                        hasDraft && "border-green-500 bg-green-500",
                                        !hasDraft && "border-gray-300 bg-white"
                                      )}
                                    >
                                      {hasDraft && <CheckIcon size={12} color="white" />}
                                    </div>
                                    <span className="text-xs text-black">Gửi bản nháp</span>
                                  </div>
                                )}
                                <div className="flex items-center gap-2">
                                  <div
                                    className={cn(
                                      "w-3 h-3 rounded-full border flex items-center justify-center",
                                      hasSubmitUrl && "border-green-500 bg-green-500",
                                      !hasSubmitUrl && "border-gray-300 bg-white"
                                    )}
                                  >
                                    {hasSubmitUrl && <CheckIcon size={12} color="white" />}
                                  </div>
                                  <span className="text-xs text-black">Gửi đường dẫn bài đăng</span>
                                </div>

                                {/* Show sub-versions if exist (1.1, 1.2, 2.1, etc.) */}
                                {subVersions.length > 0 && (
                                  <div className="ml-4 space-y-2 mt-2">
                                    {subVersions.map((subDraft) => {
                                      const subVersion = subDraft.version
                                      const hasSubDraft = !!subDraft
                                      const hasSubSubmitUrl = !!subDraft?.submitUrl
                                      const subStatus = subDraft?.status
                                      return (
                                        <div key={subVersion} className="space-y-2">
                                          <div className="flex items-center gap-2">
                                            <div
                                              className={cn(
                                                "w-3.5 h-3.5 rounded-full border flex items-center justify-center",
                                                hasSubDraft && "border-green-500 bg-green-500",
                                                !hasSubDraft && "border-gray-300 bg-white"
                                              )}
                                            >
                                              {hasSubDraft && <CheckIcon size={10} color="white" />}
                                            </div>
                                            <span className="text-xs font-medium text-black">
                                              Phiên bản {subVersion}
                                              {!!subStatus && (
                                                <span
                                                  className={TEXT_STATUS_DRAFT_COLOR[subStatus]}
                                                >
                                                  {" "}
                                                  {TEXT_STATUS_DRAFT[subStatus]}
                                                </span>
                                              )}
                                            </span>
                                          </div>
                                          {isCast && (
                                            <div className="ml-4 flex items-center gap-2">
                                              <div
                                                className={cn(
                                                  "w-3 h-3 rounded-full border flex items-center justify-center",
                                                  hasSubDraft && "border-green-500 bg-green-500",
                                                  !hasSubDraft && "border-gray-300 bg-white"
                                                )}
                                              >
                                                {hasSubDraft && (
                                                  <CheckIcon size={8} color="white" />
                                                )}
                                              </div>
                                              <span className="text-xs text-black">
                                                Gửi bản nháp
                                              </span>
                                            </div>
                                          )}
                                          <div className="ml-4 flex items-center gap-2">
                                            <div
                                              className={cn(
                                                "w-3 h-3 rounded-full border flex items-center justify-center",
                                                hasSubSubmitUrl && "border-green-500 bg-green-500",
                                                !hasSubSubmitUrl && "border-gray-300 bg-white"
                                              )}
                                            >
                                              {hasSubSubmitUrl && (
                                                <CheckIcon size={8} color="white" />
                                              )}
                                            </div>
                                            <span className="text-xs text-gray-800">
                                              Gửi đường dẫn bài đăng
                                            </span>
                                          </div>
                                        </div>
                                      )
                                    })}
                                  </div>
                                )}
                              </div>
                            </div>
                          )
                        })
                      })()}
                    </div>
                  )}
                </div>
              </div>
            ))}
      </div>
    </div>
  )
}

// Submission Action Button Component (same as in submission.tsx)
interface SubmissionActionButtonProps {
  isLoading: boolean
  status?: ESubmitStatus
  isAuthCreatorTap: boolean
  onAuthorize: () => void
  onManageDrafts: () => void
  isCast: boolean
  link?: string
  isSendOutside?: boolean
}

const SubmissionActionButton = ({
  isLoading,
  status,
  isAuthCreatorTap,
  onAuthorize,
  onManageDrafts,
  isCast,
  link,
  isSendOutside
}: SubmissionActionButtonProps) => {
  if (isLoading) {
    return (
      <Button
        disabled={true}
        className="flex-1 h-12 bg-gray-200 border border-primary-800 text-primary-800 rounded-xl font-medium"
      >
        <LoadingSpinner className="w-4 h-4" />
      </Button>
    )
  }

  if (status === ESubmitStatus.FINISHED) {
    return (
      <Button
        onClick={onManageDrafts}
        className="flex-1 h-12 bg-primary-800 rounded-xl font-medium"
      >
        Đã hoàn thành
      </Button>
    )
  }

  if (status === ESubmitStatus.NOT_AUTHORIZED_ORDER_SAMPLE) {
    return (
      <Button onClick={onAuthorize} className="flex-1 h-12 bg-primary-800 rounded-xl font-medium">
        Ủy quyền ngay
      </Button>
    )
  }

  if (
    status &&
    [
      ESubmitStatus.WORKING,
      ESubmitStatus.OVERDUE,
      ESubmitStatus.REJECTED_ACCEPTANCE,
      ESubmitStatus.ACCEPTED_ACCEPTANCE
    ].includes(status)
  ) {
    return (
      <Button
        onClick={onManageDrafts}
        className="flex-1 h-12 bg-primary-800 rounded-xl font-medium px-2 min-w-0"
      >
        <span className="truncate w-full text-center">
          {isCast ? "Quản lý bản nháp và đường dẫn" : "Quản lý đường dẫn"}
        </span>
      </Button>
    )
  }

  // Chỉ hiển thị cho cast campaign
  if (isCast && status === ESubmitStatus.PENDING_ACCEPTANCE) {
    return (
      <Button
        onClick={onManageDrafts}
        className="flex-1 h-12 bg-primary-800 rounded-xl font-medium"
      >
        Đang đợi nghiệm thu
      </Button>
    )
  }

  if (!isAuthCreatorTap) {
    return (
      <Button onClick={onAuthorize} className="flex-1 h-12 bg-primary-800 rounded-xl font-medium">
        Ủy quyền ngay
      </Button>
    )
  }

  // Camp gửi ngoài sàn TikTok: nhãn hàng tự gửi mẫu, không cho yêu cầu mẫu
  if (isSendOutside) {
    return null
  }

  return (
    <Button
      onClick={() => link && sendOpenLink(link)}
      className="flex-1 h-12 bg-primary-800 rounded-xl font-medium"
    >
      Yêu cầu mẫu
    </Button>
  )
}

export { SubmissionDetailsSheet }
